/**
 * InvestmentIntelligenceDashboard
 *
 * A modern dashboard card that visualises a plot's investment story:
 *  · Animated KPI tiles with counting numbers
 *  · Interactive donut showing ROI composition (appreciation vs rent vs infra)
 *  · Horizontal bar-race showing 5-yr price trajectory
 *
 * All colours pull from the AcreMitra amber / emerald / slate palette so it
 * matches the rest of the Locality report card.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, IndianRupee, Layers, Sparkles, Zap, Building2 } from 'lucide-react';

// --- little counting hook (no external deps) --------------------------------
const useCountUp = (target, duration = 900) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target == null || isNaN(target)) return undefined;
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
};

const fmtINRShort = (n) => {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n >= 1e8 ? 1 : 2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n >= 1e6 ? 1 : 2)} L`;
  if (n >= 1e3) return `₹${Math.round(n / 1e3)} K`;
  return `₹${Math.round(n)}`;
};

// --- ROI composition donut --------------------------------------------------
const SEGMENTS = [
  { key: 'appreciation', label: 'Land appreciation', color: '#059669', pct: 0.55 },
  { key: 'rental',       label: 'Rental yield',      color: '#0284c7', pct: 0.20 },
  { key: 'infra',        label: 'Infra premium',     color: '#d97706', pct: 0.15 },
  { key: 'liquidity',    label: 'Liquidity premium', color: '#7c3aed', pct: 0.10 },
];

const Donut = ({ size = 148, stroke = 18, roiTotal = 0 }) => {
  const R = (size - stroke) / 2;
  const C = 2 * Math.PI * R;
  const totalV = useCountUp(roiTotal, 900);
  let cum = 0;
  const cx = size / 2;
  const cy = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="ROI composition">
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
      {SEGMENTS.map((seg) => {
        const dash = seg.pct * C;
        const gap = C - dash;
        const rot = (cum / 1) * 360 - 90;
        cum += seg.pct;
        return (
          <circle
            key={seg.key}
            cx={cx} cy={cy} r={R}
            fill="none"
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`}
            strokeLinecap="butt"
            transform={`rotate(${rot} ${cx} ${cy})`}
            style={{ transition: 'stroke-dasharray 700ms ease' }}
          />
        );
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize="10" fontWeight="800" fill="#64748b"
            style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        10-yr ROI
      </text>
      <text x={cx} y={cy + 15} textAnchor="middle" fontSize="22" fontWeight="900" fill="#0f172a">
        {totalV.toFixed(0)}%
      </text>
    </svg>
  );
};

// --- horizontal bar race for 5-year price trajectory -----------------------
const BarRace = ({ current, projected, roi }) => {
  const years = 5;
  const rate = roi > 0 ? Math.pow((100 + roi) / 100, 1 / 10) - 1 : 0.09; // fallback 9% CAGR
  const points = useMemo(() =>
    Array.from({ length: years + 1 }, (_, i) => ({
      year: i,
      value: (current || 0) * Math.pow(1 + rate, i * 2), // yr 0,2,4,6,8,10
    })),
    [current, rate],
  );
  const max = points[points.length - 1].value || 1;
  return (
    <div className="w-full">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
        Projected value ladder
      </div>
      <div className="space-y-1.5">
        {points.map((p, i) => {
          const pct = (p.value / max) * 100;
          const label = `Yr ${i * 2}`;
          return (
            <div key={label} className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-500 w-8 shrink-0">{label}</span>
              <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden relative">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${pct}%`,
                    background: `linear-gradient(90deg,#065f46 0%,#059669 45%,#facc15 90%)`,
                    boxShadow: '0 0 12px rgba(5,150,105,0.35)',
                    animation: `am-bar-grow 800ms ease ${i * 90}ms both`,
                  }}
                />
              </div>
              <span className="text-[11px] font-black text-slate-700 shrink-0 w-16 text-right tabular-nums">
                {fmtINRShort(p.value)}
              </span>
            </div>
          );
        })}
      </div>
      <style>{`@keyframes am-bar-grow { from { width: 0 } }`}</style>
    </div>
  );
};

// --- Main component ---------------------------------------------------------
const InvestmentIntelligenceDashboard = ({ projected, roi, rental }) => {
  const current = Number(projected?.current_price) || 0;
  const future  = Number(projected?.projected_price) || 0;
  const roiPct  = Number(projected?.roi_percent) || (current && future ? ((future - current) / current) * 100 : 0);
  const rentalYr = Number(rental?.annual_estimate) || 0;

  const kpiRoi = useCountUp(roiPct);
  const kpiFuture = useCountUp(future);
  const kpiCagr = useCountUp(
    roiPct > 0 ? (Math.pow((100 + roiPct) / 100, 1 / 10) - 1) * 100 : 0,
  );

  return (
    <div
      className="mt-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-white via-emerald-50/40 to-amber-50/30 p-4 shadow-sm"
      data-testid="investment-intelligence-dashboard"
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="w-8 h-8 rounded-lg bg-emerald-600 text-white grid place-items-center shadow"><TrendingUp size={16} /></span>
        <h4 className="text-sm font-black text-slate-900 uppercase tracking-wide">Investment intelligence dashboard</h4>
        <span className="text-[9px] font-black uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5 ml-auto">
          <Sparkles size={9} className="inline mr-0.5" /> Live
        </span>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-xl border border-emerald-200 bg-white p-3 hover:shadow-md transition-all">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">10-yr ROI</div>
          <div className="text-2xl font-black text-emerald-700 tabular-nums leading-tight">{kpiRoi.toFixed(0)}%</div>
          <div className="text-[10px] text-slate-500 mt-0.5 inline-flex items-center gap-1"><TrendingUp size={9} /> total upside</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-white p-3 hover:shadow-md transition-all">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">CAGR</div>
          <div className="text-2xl font-black text-amber-600 tabular-nums leading-tight">{kpiCagr.toFixed(1)}%</div>
          <div className="text-[10px] text-slate-500 mt-0.5 inline-flex items-center gap-1"><Zap size={9} /> per year</div>
        </div>
        <div className="rounded-xl border border-sky-200 bg-white p-3 hover:shadow-md transition-all">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Future value</div>
          <div className="text-2xl font-black text-sky-700 tabular-nums leading-tight">{fmtINRShort(kpiFuture)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5 inline-flex items-center gap-1"><Building2 size={9} /> in 10 yrs</div>
        </div>
      </div>

      {/* Donut + Bar race */}
      <div className="grid md:grid-cols-2 gap-4 items-start">
        <div className="flex items-center gap-3 rounded-xl bg-white/70 border border-slate-200 p-3">
          <Donut roiTotal={roiPct} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
              ROI composition
            </div>
            <ul className="space-y-1">
              {SEGMENTS.map((s) => (
                <li key={s.key} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="text-slate-700 flex-1 truncate">{s.label}</span>
                  <span className="text-slate-500 font-bold tabular-nums">{(s.pct * 100).toFixed(0)}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="rounded-xl bg-white/70 border border-slate-200 p-3">
          <BarRace current={current} projected={future} roi={roiPct} />
          {rentalYr > 0 && (
            <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex items-center gap-2 text-[11px] text-slate-600">
              <Layers size={12} className="text-emerald-700" />
              <span>Annual rental — <strong className="text-emerald-800">{fmtINRShort(rentalYr)}</strong></span>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <IndianRupee size={9} /> yield
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentIntelligenceDashboard;
